/**
 * The case Time pane: hours and what they're worth against the fee, a live
 * row while the tracker counts this case, hours by timekeeper, the case's
 * entries and its expenses. Your unreleased time is included and visible
 * only to you.
 */
import { AlertTriangle, Lock, Plus } from "lucide-react";
import { useState, type FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { timeActions } from "../actions";
import { CaseDot } from "../components/CasePicker";
import { HoursStepper } from "../components/HoursStepper";
import { useCaseTime } from "../queries";
import { timePathFor } from "../routes";
import { useTrackerStore } from "../tracker/trackerStore";
import type { CaseTimeSummary, TimeEntry } from "../types";
import { formatHours, formatMoney, isUnreleased, todayIso } from "../utils";
import { Segmented } from "../settings/SettingRow";
import { CaseExpenses } from "./CaseExpenses";
import { CaseRules } from "./CaseRules";
import { compareToFee, FEE_OPTIONS, feeLine, liveHours } from "./caseModel";

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const Stat: FC<{ label: string; value: string; warn?: boolean }> = ({
  label,
  value,
  warn
}) => (
  <span className="flex flex-col">
    <span className="text-sm text-fg-3">{label}</span>
    <span
      className={cn(
        "text-2xl font-medium tracking-tight tabular-nums",
        warn && "text-warn"
      )}
    >
      {value}
    </span>
  </span>
);

const AddTime: FC<{ individualId: string }> = ({ individualId }) => {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(0.5);
  const [narrative, setNarrative] = useState("");
  const add = async () => {
    const entry = await timeActions.create({
      work_date: todayIso(),
      hours,
      narrative: narrative.trim() || undefined,
      individual_id: individualId
    });
    if (entry) {
      setOpen(false);
      setNarrative("");
      setHours(0.5);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="xs" variant="secondary">
          <Plus /> Add time
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-2.5">
        <span className="text-base font-medium">Add time for today</span>
        <HoursStepper
          value={hours}
          onChange={setHours}
          className="self-start"
        />
        <Input
          aria-label="What you did"
          placeholder="What you did"
          value={narrative}
          onChange={(e) => setNarrative(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void add();
          }}
        />
        <Button variant="primary" size="sm" onClick={() => void add()}>
          Add {formatHours(hours)}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

const FeeControl: FC<{ summary: CaseTimeSummary }> = ({ summary }) => {
  const value =
    FEE_OPTIONS.find((o) => o.id === summary.fee_arrangement)?.id ?? "hourly";
  const saved = summary.matter.flat_fee ?? null;
  return (
    <span className="flex items-center gap-2">
      <Segmented
        label="Fee arrangement"
        value={value}
        options={FEE_OPTIONS}
        onChange={(next) =>
          void timeActions.saveCaseBilling(summary.individual_id, {
            fee_arrangement: next
          })
        }
      />
      {value === "flat_fee" ? (
        <span className="relative flex items-center">
          <span className="pointer-events-none absolute left-2 text-sm text-fg-3">
            $
          </span>
          <Input
            key={String(saved)}
            aria-label="Flat fee"
            inputMode="decimal"
            placeholder="Amount"
            defaultValue={saved ?? ""}
            className="h-7 w-24 pl-5 text-sm"
            onBlur={(e) => {
              const n = Number(e.currentTarget.value.replace(/[^0-9.]/g, ""));
              const next = e.currentTarget.value.trim() && n > 0 ? n : null;
              if (next !== saved)
                void timeActions.saveCaseBilling(summary.individual_id, {
                  flat_fee: next
                });
            }}
          />
        </span>
      ) : null}
    </span>
  );
};

type Props = { individualId: string };

export const CaseTimePane: FC<Props> = ({ individualId }) => {
  const navigate = useNavigate();
  const enabled = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectTimeTrackingEnabled(s)
  );
  const me = useSelector((s: LawyerReducer) => lawyerSelectors.selectUid(s));
  const query = useCaseTime(individualId, enabled);
  const context = useTrackerStore((s) => s.context);
  const seconds = useTrackerStore((s) => s.seconds);
  const counting = useTrackerStore((s) => s.counting);
  const paused = useTrackerStore((s) => s.paused);

  if (!enabled) {
    return (
      <Empty className="h-full" data-testid="case-time-off">
        <EmptyHeader>
          <EmptyTitle>Time tracking is off</EmptyTitle>
          <EmptyDescription>
            A firm admin can turn it on in Settings, under Time &amp; billing.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }
  if (query.isLoading || !query.data) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-60" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const s = query.data;
  const live =
    context?.individual_id === individualId && counting && !paused
      ? liveHours(seconds)
      : 0;
  const total = s.total_hours + live;
  const fee = compareToFee(s);
  const maxHours = Math.max(0.1, ...s.by_timekeeper.map((r) => r.hours));
  const isMine = (e: TimeEntry) => Boolean(me) && e.user_id === me;
  const openEntry = (e: TimeEntry) =>
    navigate(timePathFor({ view: "day", date: e.work_date, entry: e.id }));

  return (
    <div
      className="@container flex h-full min-h-0 flex-col gap-6 overflow-y-auto bg-surface px-6 py-5"
      data-testid="case-time-pane"
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-lg font-medium tracking-tight">
            Time on this case
          </span>
          <span className="text-sm text-fg-3">{feeLine(s)}</span>
        </span>
        <span className="flex-1" />
        <FeeControl summary={s} />
        <AddTime individualId={individualId} />
      </div>

      <div className="grid grid-cols-1 gap-5 @2xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] @2xl:gap-7">
        <div className="flex flex-col gap-3">
          <span className="flex gap-7">
            <Stat label="Hours tracked" value={formatHours(total)} />
            {fee && s.effective_rate ? (
              <Stat
                label="Effective rate"
                value={`${formatMoney(s.effective_rate)}/h`}
                warn={fee.over}
              />
            ) : null}
            <Stat
              label={
                s.fee_arrangement === "hourly" ||
                s.fee_arrangement === "hourly_capped"
                  ? "Billable value"
                  : "Value at standard rates"
              }
              value={formatMoney(
                s.fee_arrangement === "hourly" ||
                  s.fee_arrangement === "hourly_capped"
                  ? s.team_amount
                  : s.standard_value
              )}
            />
          </span>
          {fee ? (
            <div className="flex flex-col gap-1.5" data-testid="case-fee-bar">
              <span className="flex text-sm text-fg-3">
                <span>Time value against the flat fee</span>
                <span className="flex-1" />
                <span className="tabular-nums">
                  {formatMoney(fee.value)} of {formatMoney(fee.fee)}
                </span>
              </span>
              <span className="flex h-2.5 overflow-hidden rounded-full bg-fill-2">
                <span
                  className={fee.over ? "bg-warn" : "bg-primary"}
                  style={{ width: `${fee.pct}%` }}
                />
              </span>
              <span
                className={cn(
                  "flex items-center gap-1.5 text-sm",
                  fee.over ? "text-warn" : "text-fg-3"
                )}
              >
                {fee.over ? <AlertTriangle className="size-3.5" /> : null}
                {fee.line}
              </span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg-3">
            Hours by timekeeper
          </span>
          {s.by_timekeeper.length ? (
            s.by_timekeeper.map((r) => (
              <span
                key={r.user_id}
                className="grid grid-cols-[110px_minmax(0,1fr)_44px] items-center gap-2.5 text-sm"
              >
                <span className="truncate">
                  {r.user_id === me ? "You" : r.name.split(" ")[0]}
                </span>
                <span className="h-2 overflow-hidden rounded-full bg-fill-2">
                  <span
                    className={cn(
                      "block h-full bg-primary",
                      r.user_id !== me && "opacity-45"
                    )}
                    style={{ width: `${(r.hours / maxHours) * 100}%` }}
                  />
                </span>
                <span className="text-right font-medium tabular-nums">
                  {formatHours(r.hours)}
                </span>
              </span>
            ))
          ) : (
            <span className="text-sm text-fg-3">No time on this case yet.</span>
          )}
          {s.my_unreleased_hours > 0 ? (
            <span className="text-sm text-fg-3">
              Includes your unreleased time, which only you can see.
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="flex items-center">
          <span className="text-base font-medium">Entries</span>
          <span className="flex-1" />
          <Button
            size="xs"
            variant="link"
            onClick={() => navigate(timePathFor())}
          >
            Review mine in Time
          </Button>
        </span>
        <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
          <Table className="table-fixed text-base">
            <TableHeader className="bg-fill-1">
              <TableRow>
                <TableHead className="h-8 w-16 text-sm">Date</TableHead>
                <TableHead className="h-8 w-20 text-sm @xl:w-28">Who</TableHead>
                <TableHead className="h-8 text-sm">Narrative</TableHead>
                <TableHead className="hidden h-8 w-14 text-sm @xl:table-cell">
                  Code
                </TableHead>
                <TableHead className="h-8 w-14 text-right text-sm">
                  Hours
                </TableHead>
                <TableHead className="h-8 w-24 text-sm @xl:w-28">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {live ? (
                <TableRow className="bg-fill-1" data-testid="case-live-row">
                  <TableCell className="text-fg-3">Now</TableCell>
                  <TableCell>You</TableCell>
                  <TableCell className="text-fg-3">
                    <span className="flex items-center gap-1.5 truncate">
                      <CaseDot
                        id={individualId}
                        className="animate-pulse rounded-full"
                      />
                      Tracking now. Becomes a suggestion when you leave the
                      case.
                    </span>
                  </TableCell>
                  <TableCell className="hidden @xl:table-cell" />
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatHours(live)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="h-5 text-sm">
                      Live
                    </Badge>
                  </TableCell>
                </TableRow>
              ) : null}
              {s.entries.map((e) => {
                const mine = isMine(e);
                return (
                  <TableRow
                    key={e.id}
                    data-testid="case-entry-row"
                    className={mine ? "cursor-pointer" : undefined}
                    onClick={mine ? () => openEntry(e) : undefined}
                  >
                    <TableCell className="text-fg-3 tabular-nums">
                      {shortDate(e.work_date)}
                    </TableCell>
                    <TableCell className="truncate">
                      {mine ? "You" : (e.user_name ?? "").split(" ")[0]}
                    </TableCell>
                    <TableCell className="truncate">{e.narrative}</TableCell>
                    <TableCell className="hidden font-mono text-sm text-fg-3 @xl:table-cell">
                      {e.utbms_activity}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatHours(e.duration_hours)}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Badge
                          variant="secondary"
                          className="h-5 text-sm font-normal"
                        >
                          {e.status_label}
                        </Badge>
                        {mine && isUnreleased(e.status) ? (
                          <Lock
                            className="size-3 text-fg-3"
                            aria-label="Only you can see this"
                          />
                        ) : null}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!live && !s.entries.length ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-6 text-center text-sm whitespace-normal text-fg-3"
                  >
                    Time shows up here as you and your team work on this case.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </div>

      <CaseExpenses
        individualId={individualId}
        companyCase={Boolean(s.matter.company_id)}
      />

      <CaseRules individualId={individualId} />
    </div>
  );
};
